import { Banknote, Delete } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const fmt = (cents) =>
  (cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Modal de cálculo de troco (v1.5.518) — abre ao escolher DINHEIRO.
 *
 * Digitação estilo caixa registradora (pedido do dono): o valor é SEMPRE em
 * R$, começa pelo centavo e cada dígito digitado empurra os anteriores pra
 * esquerda — 1 → 0,01 · 5 → 0,15 · 0 → 1,50 · 0 → 15,00. Implementação: o
 * estado é um inteiro de CENTAVOS; qualquer edição do input reduz o texto a
 * dígitos e reinterpreta como centavos, então colar "15" também dá R$ 0,15.
 */
export default function TrocoModal({ open, onOpenChange, total, onConfirm }) {
  const [cents, setCents] = useState(0);

  useEffect(() => {
    if (open) setCents(0); // sempre começa zerado, pronto pro primeiro centavo
  }, [open]);

  const totalCents = Math.round((Number(total) || 0) * 100);
  const troco = cents - totalCents;
  const suficiente = cents >= totalCents;

  const onChange = (e) => {
    const digits = String(e.target.value).replace(/\D/g, "").slice(0, 9); // teto R$ 9.999.999,99
    setCents(parseInt(digits || "0", 10));
  };

  const confirmar = () => {
    if (!suficiente) return;
    onConfirm((cents / 100).toFixed(2));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-sm rounded-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-green-500 text-white flex items-center justify-center">
              <Banknote className="w-4 h-4" />
            </span>
            Pagamento em dinheiro
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-1">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total a pagar</span>
            <span className="font-bold tabular-nums">R$ {fmt(totalCents)}</span>
          </div>

          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
              Valor recebido
            </p>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-muted-foreground">
                R$
              </span>
              <input
                type="text"
                inputMode="numeric"
                autoFocus
                value={fmt(cents)}
                onChange={onChange}
                onKeyDown={(e) => { if (e.key === "Enter") confirmar(); }}
                className="w-full h-14 pl-12 pr-4 rounded-xl border-2 border-green-500/50 focus:border-green-500 bg-background text-right text-2xl font-black tabular-nums outline-none"
              />
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Delete className="w-3 h-3" /> Backspace apaga o último dígito
              </p>
              <button
                type="button"
                onClick={() => setCents(totalCents)}
                className="text-[11px] font-semibold text-primary hover:underline"
              >
                Valor exato
              </button>
            </div>
          </div>

          {/* Troco ao vivo: verde quando cobre, aviso quando falta */}
          <div className={`rounded-xl px-4 py-3 text-center ${
            cents === 0
              ? "bg-muted/40 text-muted-foreground"
              : suficiente
                ? "bg-green-500/10 border border-green-500/30"
                : "bg-amber-500/10 border border-amber-500/30"
          }`}>
            {cents === 0 ? (
              <p className="text-sm">Digite o valor recebido do cliente</p>
            ) : suficiente ? (
              <>
                <p className="text-xs text-muted-foreground">Troco</p>
                <p className="text-2xl font-black text-green-600 dark:text-green-400 tabular-nums">
                  R$ {fmt(troco)}
                </p>
              </>
            ) : (
              <p className="text-sm font-semibold text-amber-600 dark:text-amber-400 tabular-nums">
                Faltam R$ {fmt(totalCents - cents)}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-11" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              className="h-11 font-bold bg-green-600 hover:bg-green-700 text-white"
              disabled={!suficiente}
              onClick={confirmar}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
